import { useEffect, useRef } from 'react';
import { useAuth, useOrganization } from '@clerk/react';
import { setTokenProvider, api } from '../api';
import { useUIStore } from '../../stores/ui-store';
import {
  initElectronSync,
  updateSyncToken,
  updateSyncGetToken,
  stopElectronSync,
  initWebSync,
  updateWebSyncToken,
  stopWebSync,
} from '../sync-service';

// Bridges Clerk's getToken() into the api module's token provider.
// In Electron mode also triggers offline sync after login.
export function AuthBridge() {
  const { getToken, userId } = useAuth();
  const { organization, membership } = useOrganization();
  const setIsAdmin = useUIStore((s) => s.setIsAdmin);
  const syncedOrg = useRef<string | null>(null);
  const wasSignedIn = useRef(false);

  // Mirror the Clerk membership role into the UI store so destructive-action
  // buttons (deletes — admin-only on the server) hide for editors. Defaults to
  // admin while membership resolves; the server still enforces the real check.
  useEffect(() => {
    setIsAdmin(membership?.role === 'org:admin');
  }, [membership?.role, setIsAdmin]);

  useEffect(() => {
    setTokenProvider(() => getToken());
    return () => { setTokenProvider(null); };
  }, [getToken]);

  // Stop sync on logout only — not on every getToken reference change.
  // Calling stopElectronSync on getToken change kills the timer+SSE permanently
  // because initElectronSync won't re-run (syncedOrg.current still matches).
  useEffect(() => {
    if (userId) {
      wasSignedIn.current = true;
    } else if (wasSignedIn.current) {
      wasSignedIn.current = false;
      syncedOrg.current = null;
      if (window.electronAPI) stopElectronSync();
      else stopWebSync();
    }
  }, [userId]);

  // Keep sync token AND getToken fn fresh on Clerk token refresh.
  // updateSyncGetToken prevents _getToken from going stale when Clerk issues a new function ref.
  useEffect(() => {
    if (!window.electronAPI) return;
    const freshGetToken = (forceRefresh?: boolean) => getToken({ skipCache: forceRefresh });
    updateSyncGetToken(freshGetToken);
    getToken().then(updateSyncToken);
  }, [getToken, userId]);

  // Trigger Electron sync once per org session
  useEffect(() => {
    if (!window.electronAPI) return;
    if (!organization?.id) return;
    if (syncedOrg.current === organization.id) return;

    syncedOrg.current = organization.id;

    const freshGetToken = (forceRefresh?: boolean) => getToken({ skipCache: forceRefresh });
    initElectronSync(freshGetToken, organization.id, organization.name ?? undefined, organization.slug ?? undefined).catch((err) =>
      console.error('[AuthBridge] Electron sync error:', err),
    );
  }, [organization?.id, organization?.name, organization?.slug, getToken]);

  // Sync org display name into local DB once per org session.
  // Fixes orgs auto-created by requireAuth fallback INSERT (which only has slug).
  useEffect(() => {
    if (!organization?.id || !organization.name) return;
    api.patchOrg(organization.name, organization.slug ?? organization.id).catch(() => {/* non-critical */});
  }, [organization?.id, organization?.name, organization?.slug]);

  // Web-browser SSE sync — (re)connect whenever org or token changes.
  // initWebSync kills any existing SSE before starting new, so safe to call multiple times.
  useEffect(() => {
    if (window.electronAPI) return;
    if (!organization?.id) return;

    initWebSync(getToken);

    return () => stopWebSync();
  }, [organization?.id, getToken]);

  // Keep web SSE token fresh on Clerk token refresh
  useEffect(() => {
    if (window.electronAPI) return;
    getToken().then((t) => updateWebSyncToken(t));
  }, [getToken, userId]);

  return null;
}
